import { Box, Typography, Tooltip } from "@mui/material";
import { Guest, isValidGuestStatus } from "src/types";
import { GuestStatusIndicator } from "src/componentsV2/GuestStatusIndicator";
import {
  getFollowUpStatusLabel,
  getFollowUpStatusBackgroundColor,
} from "src/utils/followUpStatus";

interface FollowUpStatusCellProps {
  guest: Guest;
  onClick?: (meetingId: number) => void;
}

/**
 * Component that displays the follow-up status for a guest.
 * It intelligently chooses between displaying:
 * 1. Guest status (followUpGuestStatus) if available and valid
 * 2. Follow-up status (followUpStatus) as fallback
 *
 * If the guest has a followUpMeetingId, the cell becomes clickable
 * and navigates to the meeting details when clicked.
 */
export function FollowUpStatusCell({
  guest,
  onClick,
}: FollowUpStatusCellProps) {
  // Use followUpGuestStatus if it's a valid GuestStatus, otherwise fall back to followUpStatus
  const useGuestStatus =
    guest.followUpGuestStatus && isValidGuestStatus(guest.followUpGuestStatus);

  if (useGuestStatus) {
    if (guest.followUpMeetingId) {
      return (
        <Tooltip
          title={`View Follow Up Meeting ${guest.followUpMeetingId} Details`}
          arrow
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (onClick) {
                onClick(guest.followUpMeetingId!);
              }
            }}
            style={{ cursor: onClick ? "pointer" : "default" }}
          >
            <GuestStatusIndicator status={guest.followUpGuestStatus} />
          </div>
        </Tooltip>
      );
    }

    return <GuestStatusIndicator status={guest.followUpGuestStatus} />;
  }

  // Fall back to FollowUpStatus display
  const statusLabel = getFollowUpStatusLabel(guest.followUpStatus);
  const backgroundColor = getFollowUpStatusBackgroundColor(
    guest.followUpStatus,
  );

  const statusBox = (
    <Box
      sx={{
        width: "110px",
        borderRadius: "4px",
        px: "2px",
        py: "8px",
        textAlign: "center",
        backgroundColor,
        cursor: guest.followUpMeetingId && onClick ? "pointer" : "default",
      }}
      onClick={
        guest.followUpMeetingId && onClick
          ? (e) => {
              e.stopPropagation();
              onClick(guest.followUpMeetingId!);
            }
          : undefined
      }
    >
      <Typography sx={{ fontSize: "12px", color: "white" }} component="span">
        {statusLabel}
      </Typography>
    </Box>
  );

  if (guest.followUpMeetingId) {
    return (
      <Tooltip title={`Follow Up Meeting ID: ${guest.followUpMeetingId}`} arrow>
        {statusBox}
      </Tooltip>
    );
  }

  return statusBox;
}
